import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AppState, Account, RecurringPayment, OneOffPayment, Pot, PaydayRule, CreditCard } from '../types';
import { loadAppState, saveAppState } from '../utils/storage';

type AppAction =
  | { type: 'SET_ACCOUNT'; payload: Account }
  | { type: 'ADD_RECURRING_PAYMENT'; payload: Omit<RecurringPayment, 'id'> }
  | { type: 'UPDATE_RECURRING_PAYMENT'; payload: { id: string; payment: Partial<RecurringPayment> } }
  | { type: 'DELETE_RECURRING_PAYMENT'; payload: string }
  | { type: 'ADD_ONE_OFF_PAYMENT'; payload: Omit<OneOffPayment, 'id'> }
  | { type: 'UPDATE_ONE_OFF_PAYMENT'; payload: { id: string; payment: Partial<OneOffPayment> } }
  | { type: 'DELETE_ONE_OFF_PAYMENT'; payload: string }
  | { type: 'ADD_POT'; payload: Omit<Pot, 'id'> }
  | { type: 'UPDATE_POT'; payload: { id: string; pot: Partial<Pot> } }
  | { type: 'DELETE_POT'; payload: string }
  | { type: 'ADD_PAYDAY_RULE'; payload: Omit<PaydayRule, 'id'> }
  | { type: 'UPDATE_PAYDAY_RULE'; payload: { id: string; rule: Partial<PaydayRule> } }
  | { type: 'DELETE_PAYDAY_RULE'; payload: string }
  | { type: 'ADD_CREDIT_CARD'; payload: Omit<CreditCard, 'id'> }
  | { type: 'UPDATE_CREDIT_CARD'; payload: { id: string; creditCard: Partial<CreditCard> } }
  | { type: 'DELETE_CREDIT_CARD'; payload: string }
  | { type: 'LOAD_STATE'; payload: AppState };

const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'LOAD_STATE':
      return {
        ...action.payload,
        account: {
          ...action.payload.account,
          lastUpdated: (() => {
            const lastUpdated = action.payload.account.lastUpdated;
            if (lastUpdated instanceof Date) return lastUpdated;
            if (!lastUpdated) return new Date();
            const date = new Date(lastUpdated);
            return isNaN(date.getTime()) ? new Date() : date;
          })()
        }
      };
      
    case 'SET_ACCOUNT':
      return {
        ...state,
        account: { ...action.payload, lastUpdated: new Date() }
      };
      
    case 'ADD_RECURRING_PAYMENT':
      return {
        ...state,
        recurringPayments: [
          ...state.recurringPayments,
          { ...action.payload, id: generateId() }
        ]
      };
      
    case 'UPDATE_RECURRING_PAYMENT':
      return {
        ...state,
        recurringPayments: state.recurringPayments.map(payment =>
          payment.id === action.payload.id
            ? { ...payment, ...action.payload.payment }
            : payment
        )
      };
      
    case 'DELETE_RECURRING_PAYMENT':
      return {
        ...state,
        recurringPayments: state.recurringPayments.filter(
          payment => payment.id !== action.payload
        )
      };
      
    case 'ADD_ONE_OFF_PAYMENT':
      return {
        ...state,
        oneOffPayments: [
          ...state.oneOffPayments,
          { ...action.payload, id: generateId() }
        ]
      };
      
    case 'UPDATE_ONE_OFF_PAYMENT':
      return {
        ...state,
        oneOffPayments: state.oneOffPayments.map(payment =>
          payment.id === action.payload.id
            ? { ...payment, ...action.payload.payment }
            : payment
        )
      };
      
    case 'DELETE_ONE_OFF_PAYMENT':
      return {
        ...state,
        oneOffPayments: state.oneOffPayments.filter(
          payment => payment.id !== action.payload
        )
      };
      
    case 'ADD_POT':
      return {
        ...state,
        pots: [...state.pots, { ...action.payload, id: generateId() }]
      };
      
    case 'UPDATE_POT':
      return {
        ...state,
        pots: state.pots.map(pot =>
          pot.id === action.payload.id
            ? { ...pot, ...action.payload.pot }
            : pot
        )
      };
      
    case 'DELETE_POT':
      return {
        ...state,
        pots: state.pots.filter(pot => pot.id !== action.payload)
      };
      
    case 'ADD_PAYDAY_RULE':
      return {
        ...state,
        paydayRules: [...state.paydayRules, { ...action.payload, id: generateId() }]
      };
      
    case 'UPDATE_PAYDAY_RULE':
      return {
        ...state,
        paydayRules: state.paydayRules.map(rule =>
          rule.id === action.payload.id
            ? { ...rule, ...action.payload.rule }
            : rule
        )
      };
      
    case 'DELETE_PAYDAY_RULE':
      return {
        ...state,
        paydayRules: state.paydayRules.filter(rule => rule.id !== action.payload)
      };

    case 'ADD_CREDIT_CARD':
      return {
        ...state,
        creditCards: [...state.creditCards, { ...action.payload, id: generateId() }]
      };

    case 'UPDATE_CREDIT_CARD':
      return {
        ...state,
        creditCards: state.creditCards.map(card =>
          card.id === action.payload.id
            ? { ...card, ...action.payload.creditCard }
            : card
        )
      };

    case 'DELETE_CREDIT_CARD':
      return {
        ...state,
        creditCards: state.creditCards.filter(card => card.id !== action.payload)
      };

    default:
      return state;
  }
};

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, loadAppState());

  useEffect(() => {
    const savedState = loadAppState();
    dispatch({ type: 'LOAD_STATE', payload: savedState });
  }, []);

  useEffect(() => {
    saveAppState(state);
  }, [state]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};